import React, { useMemo, useState } from "react";

import { Building2, Info, Loader2 } from "lucide-react";

import { formatDateTime, formatRelative, errorText } from "./format";
import OrgSelect from "./OrgSelect";
import {
  type ActionsSurface,
  effectiveSurface,
  type OrganizationsScope,
  type OrgPresentationDraft,
  type ServiceMode,
  type SignalAdminConfigResponse,
  type SignalAdminDraft,
  type SignalAdminOrganization,
  type SignalAdminOrgPresentation,
  type SignalAdminOverview,
  type SignalAdminVersion,
  stricterSurface,
  VALUE_LABEL,
  valueLabel,
} from "./types";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  applySignalAdminOrgPresentation,
  clearSignalAdminOrgPresentation,
  previewSignalAdminOrgPresentation,
} from "../../Store/Reducers/SignalAdminSlice";

interface OverviewPanelProps {
  current: SignalAdminConfigResponse;
  draft: SignalAdminDraft;
  onDraftChange: (next: SignalAdminDraft) => void;
  overview: SignalAdminOverview | null;
  recentVersions: SignalAdminVersion[];
  onOpenAudit: () => void;
  organizations: SignalAdminOrganization[];
  orgsLoading: boolean;
  selectedOrgId: string;
  selectedOrgName?: string;
  onOrgChange: (orgId: string) => void;
  orgPresentation: SignalAdminOrgPresentation | null;
  orgDraft: OrgPresentationDraft | null;
  onOrgDraftChange: (next: OrgPresentationDraft) => void;
  orgPresentationLoading: boolean;
  onOrgPresentationApplied: () => void;
}

const MODE_OPTIONS: {
  value: ServiceMode;
  label: string;
  description: string;
}[] = [
  {
    value: "active",
    label: "Active",
    description:
      "Signals are evaluated and shown to users in every organization.",
  },
  {
    value: "inactive",
    label: "Inactive",
    description:
      "Signals keep being evaluated in the background for every organization, but nothing is shown to users. Open signals reappear when the platform is switched back to Active.",
  },
];

const ORG_MODE_OPTIONS: {
  value: ServiceMode;
  label: string;
  description: string;
}[] = [
  {
    value: "active",
    label: "Active",
    description:
      "Signals are evaluated and shown to users in this organization (unless platform policy hides them).",
  },
  {
    value: "inactive",
    label: "Inactive",
    description:
      "Signals keep being evaluated for this organization, but nothing is shown to its users. Open signals reappear when this organization is switched back to Active.",
  },
];

const ORGANIZATIONS_OPTIONS: {
  value: OrganizationsScope;
  label: string;
  description: string;
}[] = [
  {
    value: "all",
    label: "Every organization",
    description:
      "Every organization follows the Signal Service and Open Actions above, unless its own override is stricter.",
  },
  {
    value: "opted_in",
    label: "Only organizations turned on",
    description:
      "Signals keep being evaluated everywhere, but users see them only in organizations you turn on below. Every other organization — including new ones — shows nothing, not even a “turned off” notice.",
  },
];

/** Under "Only organizations turned on", an organization's Signal Service
 * reads as whether it is turned on. */
const TURNED_ON_LABEL: Record<ServiceMode, string> = {
  active: "Turned on",
  inactive: "Off — not turned on",
};

const OrgSwitch: React.FC<{
  on: boolean;
  label: string;
  onChange: (on: boolean) => void;
}> = ({ on, label, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={() => onChange(!on)}
    className="flex items-center gap-2"
  >
    <span
      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
        on ? "bg-emerald-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
          on ? "left-[18px]" : "left-0.5"
        }`}
      />
    </span>
    <span className="text-[12.5px] font-semibold text-gray-700">
      {TURNED_ON_LABEL[on ? "active" : "inactive"]}
    </span>
  </button>
);

const SURFACE_OPTIONS: {
  value: ActionsSurface;
  label: string;
  description: string;
}[] = [
  {
    value: "enabled",
    label: "Enabled",
    description:
      "Today and Actions show signal cards, urgency and calls to action.",
  },
  {
    value: "disabled",
    label: "Disabled",
    description:
      "Today and Actions stay in the navigation but are not clickable. No signal output anywhere.",
  },
  {
    value: "hidden",
    label: "Hidden",
    description:
      "The Today and Actions tabs have been removed. Signal cards, counts, and action CTAs are no longer displayed anywhere.",
  },
];

const TIER_CHIPS: {
  key: keyof SignalAdminOverview["open_signals_by_tier"];
  label: string;
  className: string;
}[] = [
  { key: "critical", label: "Critical", className: "bg-red-50 text-red-700" },
  {
    key: "warning",
    label: "Warning",
    className: "bg-amber-50 text-amber-700",
  },
  {
    key: "opp",
    label: "Opportunity",
    className: "bg-emerald-50 text-emerald-700",
  },
  {
    key: "maint",
    label: "Maintenance",
    className: "bg-slate-100 text-slate-600",
  },
];

interface SegmentedProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

const Segmented = <T extends string>({
  label,
  value,
  options,
  onChange,
}: SegmentedProps<T>) => (
  <div
    role="radiogroup"
    aria-label={label}
    className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
  >
    {options.map(option => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className={`min-w-[84px] rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
            selected
              ? "bg-white text-brand-dark shadow-sm ring-1 ring-brand/30"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const StatusPill: React.FC<{
  tone: "good" | "muted" | "warn";
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const tones = {
    good: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    muted: "bg-gray-100 text-gray-600 ring-gray-500/20",
    warn: "bg-amber-50 text-amber-800 ring-amber-600/20",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset ${tones[tone]}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          tone === "good"
            ? "bg-emerald-500"
            : tone === "warn"
              ? "bg-amber-500"
              : "bg-gray-400"
        }`}
        aria-hidden
      />
      {children}
    </span>
  );
};

const MetricTile: React.FC<{
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
}> = ({ label, value, detail }) => (
  <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
    <div className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-gray-400">
      {label}
    </div>
    <div className="mt-1.5 text-[26px] font-bold leading-none tracking-tight text-gray-900 tabular-nums">
      {value}
    </div>
    {detail && <div className="mt-2 text-xs text-gray-500">{detail}</div>}
  </div>
);

const OverviewPanel: React.FC<OverviewPanelProps> = ({
  current,
  draft,
  onDraftChange,
  overview,
  recentVersions,
  onOpenAudit,
  organizations,
  orgsLoading,
  selectedOrgId,
  selectedOrgName,
  onOrgChange,
  orgPresentation,
  orgDraft,
  onOrgDraftChange,
  orgPresentationLoading,
  onOrgPresentationApplied,
}) => {
  const draftEffective = effectiveSurface(draft);
  const draftVisible = draftEffective === "enabled";
  const appliedVisible = current.presentation.signals_visible;
  const modeOption = MODE_OPTIONS.find(o => o.value === draft.service_mode);
  const draftOrganizations = draft.organizations ?? "all";
  const appliedOrganizations = current.config.organizations ?? "all";
  const organizationsOption = ORGANIZATIONS_OPTIONS.find(
    o => o.value === draftOrganizations
  );
  // Per-organization controls follow the applied platform setting.
  const optedIn = appliedOrganizations === "opted_in";
  const orgName = selectedOrgName || "this organization";
  const surfaceOption = SURFACE_OPTIONS.find(
    o => o.value === draft.actions_surface
  );

  const orgPending = useMemo(() => {
    if (!orgPresentation || !orgDraft) return { count: 0, payload: {} };
    const payload: {
      service_mode?: ServiceMode;
      actions_surface?: ActionsSurface;
    } = {};
    let count = 0;
    if (orgDraft.service_mode !== orgPresentation.config.service_mode) {
      payload.service_mode = orgDraft.service_mode;
      count += 1;
    }
    if (orgDraft.actions_surface !== orgPresentation.config.actions_surface) {
      payload.actions_surface = orgDraft.actions_surface;
      count += 1;
    }
    return { count, payload };
  }, [orgPresentation, orgDraft]);

  const orgModeOption = ORG_MODE_OPTIONS.find(
    o => o.value === (orgDraft?.service_mode ?? "active")
  );
  const orgSurfaceOption = SURFACE_OPTIONS.find(
    o => o.value === (orgDraft?.actions_surface ?? "enabled")
  );
  const orgDraftEffective = orgDraft ? effectiveSurface(orgDraft) : "enabled";
  // The platform is a ceiling: users get the stricter of the two.
  const platformEffective = current.presentation.effective_actions_surface;
  const orgDraftUserSurface = stricterSurface(
    platformEffective,
    orgDraftEffective
  );
  const platformTightensOrg =
    current.config.service_mode === "active" &&
    orgDraftUserSurface !== orgDraftEffective;
  const orgDraftVisible =
    current.config.service_mode === "active" &&
    orgDraftUserSurface === "enabled";
  const orgAppliedVisible =
    orgPresentation?.presentation.signals_visible ?? false;

  const [orgReviewOpen, setOrgReviewOpen] = useState(false);
  const [orgClearing, setOrgClearing] = useState(false);
  const [orgReason, setOrgReason] = useState("");
  const [orgApplying, setOrgApplying] = useState(false);
  const [orgApplyError, setOrgApplyError] = useState("");
  const [orgPreviewChanges, setOrgPreviewChanges] = useState<
    | {
        field: string;
        label: string;
        from: string | number;
        to: string | number;
      }[]
    | null
  >(null);
  const [orgPreviewError, setOrgPreviewError] = useState("");
  const dispatch = useAppDispatch();

  // Under "Only organizations turned on" a Signal Service change is the
  // organization's switch, and the confirmation names it that way.
  const orgSwitchTo =
    optedIn && !orgClearing ? orgPending.payload.service_mode : undefined;
  const changeRow = (c: {
    field: string;
    label: string;
    from: string | number;
    to: string | number;
  }) =>
    optedIn && c.field === "service_mode"
      ? {
          label: "Signals for this organization",
          from: TURNED_ON_LABEL[c.from as ServiceMode] ?? valueLabel(c.from),
          to: TURNED_ON_LABEL[c.to as ServiceMode] ?? valueLabel(c.to),
        }
      : { label: c.label, from: valueLabel(c.from), to: valueLabel(c.to) };

  const openOrgClear = () => {
    if (!selectedOrgId || !orgPresentation?.has_override) return;
    setOrgClearing(true);
    setOrgReviewOpen(true);
    setOrgReason("");
    setOrgApplyError("");
    setOrgPreviewError("");
    const { config, platform } = orgPresentation;
    // What the organization falls back to: under "Only organizations turned
    // on" that is not turned on, not the platform's own Signal Service.
    const fallback = {
      service_mode: optedIn ? ("inactive" as const) : platform.service_mode,
      actions_surface: platform.actions_surface,
    };
    setOrgPreviewChanges(
      (["service_mode", "actions_surface"] as const)
        .filter(field => config[field] !== fallback[field])
        .map(field => ({
          field,
          label: field === "service_mode" ? "Signal service" : "Open Actions",
          from: config[field],
          to: fallback[field],
        }))
    );
  };

  const openOrgReview = async () => {
    if (!selectedOrgId || orgPending.count === 0) return;
    setOrgClearing(false);
    setOrgReviewOpen(true);
    setOrgReason("");
    setOrgApplyError("");
    setOrgPreviewChanges(null);
    setOrgPreviewError("");
    try {
      const res = await dispatch(
        previewSignalAdminOrgPresentation({
          orgId: selectedOrgId,
          payload: {
            service_mode: orgDraft?.service_mode,
            actions_surface: orgDraft?.actions_surface,
          },
        })
      ).unwrap();
      setOrgPreviewChanges(res?.changes ?? []);
    } catch (error) {
      setOrgPreviewError(
        errorText(error, "Couldn't validate this organization draft.")
      );
    }
  };

  const applyOrgPresentation = async () => {
    if (!selectedOrgId || !orgDraft || !orgReason.trim()) return;
    setOrgApplying(true);
    setOrgApplyError("");
    try {
      if (orgClearing) {
        await dispatch(
          clearSignalAdminOrgPresentation({
            orgId: selectedOrgId,
            reason: orgReason.trim(),
          })
        ).unwrap();
        setOrgReviewOpen(false);
        onOrgPresentationApplied();
        return;
      }
      await dispatch(
        applySignalAdminOrgPresentation({
          orgId: selectedOrgId,
          payload: {
            service_mode: orgDraft.service_mode,
            actions_surface: orgDraft.actions_surface,
            reason: orgReason.trim(),
          },
        })
      ).unwrap();
      setOrgReviewOpen(false);
      onOrgPresentationApplied();
    } catch (error) {
      setOrgApplyError(
        errorText(error, "Couldn't apply organization settings.")
      );
    } finally {
      setOrgApplying(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section className="grid gap-4 lg:grid-cols-2">
        <article className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
                Platform operating mode
              </div>
              <h3 className="mt-1 text-[15px] font-bold text-gray-900">
                Signal Service
              </h3>
            </div>
            <StatusPill
              tone={current.config.service_mode === "active" ? "good" : "muted"}
            >
              {VALUE_LABEL[current.config.service_mode]}
            </StatusPill>
          </div>
          <div className="mt-4">
            <Segmented
              label="Signal service"
              value={draft.service_mode}
              options={MODE_OPTIONS}
              onChange={value =>
                onDraftChange({ ...draft, service_mode: value })
              }
            />
          </div>
          <p className="mt-3 text-[12.5px] leading-relaxed text-gray-600">
            {modeOption?.description}
          </p>
        </article>

        <article className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
                User presentation
              </div>
              <h3 className="mt-1 text-[15px] font-bold text-gray-900">
                Open Actions
              </h3>
            </div>
            <StatusPill
              tone={
                current.presentation.effective_actions_surface === "enabled"
                  ? "good"
                  : "muted"
              }
            >
              {VALUE_LABEL[current.presentation.effective_actions_surface]}
            </StatusPill>
          </div>
          <div className="mt-4">
            <Segmented
              label="Open Actions"
              value={draft.actions_surface}
              options={SURFACE_OPTIONS}
              onChange={value =>
                onDraftChange({ ...draft, actions_surface: value })
              }
            />
          </div>
          <p className="mt-3 text-[12.5px] leading-relaxed text-gray-600">
            {surfaceOption?.description}
          </p>
          {draft.service_mode !== "active" && (
            <div className="mt-3 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                Inactive overrides this setting — users see Open Actions as{" "}
                <strong>{VALUE_LABEL[draftEffective]}</strong>. Your saved
                choice ({VALUE_LABEL[draft.actions_surface]}) takes effect when
                the platform is Active again.
              </span>
            </div>
          )}
        </article>

        <article className="rounded-xl border border-gray-200 bg-white p-5 lg:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
                Rollout
              </div>
              <h3 className="mt-1 text-[15px] font-bold text-gray-900">
                Which organizations get signals
              </h3>
            </div>
            <StatusPill tone={appliedOrganizations === "all" ? "good" : "warn"}>
              {VALUE_LABEL[appliedOrganizations]}
            </StatusPill>
          </div>
          <div className="mt-4">
            <Segmented
              label="Which organizations get signals"
              value={draftOrganizations}
              options={ORGANIZATIONS_OPTIONS}
              onChange={value =>
                onDraftChange({ ...draft, organizations: value })
              }
            />
          </div>
          <p className="mt-3 text-[12.5px] leading-relaxed text-gray-600">
            {organizationsOption?.description}
          </p>
        </article>
      </section>

      <div
        className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-[12.5px] ${
          draftVisible
            ? "border-emerald-200 bg-emerald-50/60 text-emerald-900"
            : "border-gray-200 bg-gray-50 text-gray-700"
        }`}
      >
        <span className="font-semibold">
          {!draftVisible
            ? "Users see no signal output."
            : draftOrganizations === "opted_in"
              ? "Users see signals only in organizations turned on."
              : "Users see signals."}
        </span>
        <span className="text-gray-500">
          {draftVisible
            ? draftOrganizations === "opted_in"
              ? "Everywhere else signals are collected silently: no cards, counts or history until you turn the organization on."
              : "Cards, urgency, counts and calls to action appear across My Accounts — unless an organization override hides them."
            : "Account data, scores and the Portfolio Map stay available; read-only Action History remains. Organization overrides cannot show signals while the platform hides them."}
        </span>
        {(draftVisible !== appliedVisible ||
          draftOrganizations !== appliedOrganizations) && (
          <span className="ml-auto shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-300">
            After apply
          </span>
        )}
      </div>

      <section className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 p-4">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5 text-gray-400" aria-hidden />
              <h3 className="text-[13px] font-bold text-gray-900">
                Live snapshot
              </h3>
              <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-500 ring-1 ring-inset ring-gray-200">
                One organization
              </span>
            </div>
            <p className="mt-0.5 text-[12px] text-gray-500">
              Presentation controls and open-signal counts for a single
              organization. Layers, rules and CTAs stay platform-wide.
            </p>
          </div>
          <OrgSelect
            organizations={organizations}
            value={selectedOrgId}
            onChange={onOrgChange}
            loading={orgsLoading}
            emptyLabel="Select organization…"
            size="sm"
          />
        </div>

        {!selectedOrgId ? (
          <p className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-center text-[12.5px] text-gray-500">
            Select an organization to manage its Signal Service and Open
            Actions, and to see open signals.
          </p>
        ) : (
          <>
            {selectedOrgName && (
              <p className="mb-3 text-[12px] font-semibold text-gray-600">
                Showing {selectedOrgName}
                {orgPresentation?.has_override ? (
                  <>
                    <span className="ml-2 font-normal text-gray-400">
                      · Organization override v{orgPresentation.version}
                    </span>
                    <button
                      type="button"
                      onClick={openOrgClear}
                      disabled={orgPending.count > 0}
                      title={
                        orgPending.count > 0
                          ? "Discard the unapplied organization changes first."
                          : undefined
                      }
                      className="ml-2 font-semibold text-brand hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
                    >
                      Use platform defaults
                    </button>
                  </>
                ) : (
                  <span className="ml-2 font-normal text-gray-400">
                    {optedIn
                      ? "· No organization override — not turned on"
                      : "· Using platform defaults"}
                  </span>
                )}
              </p>
            )}

            {orgPresentationLoading && !orgDraft ? (
              <div className="mb-4 flex items-center gap-2 text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                <span className="text-xs">Loading organization settings…</span>
              </div>
            ) : orgDraft && orgPresentation ? (
              <>
                <div className="mb-4 grid gap-3 lg:grid-cols-2">
                  {optedIn ? (
                    <article className="rounded-lg border border-gray-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
                            Only organizations turned on
                          </div>
                          <h4 className="mt-1 text-[14px] font-bold text-gray-900">
                            Signals for this organization
                          </h4>
                        </div>
                        <StatusPill
                          tone={orgPresentation.turned_on ? "good" : "muted"}
                        >
                          {orgPresentation.turned_on
                            ? TURNED_ON_LABEL.active
                            : TURNED_ON_LABEL.inactive}
                        </StatusPill>
                      </div>
                      <div className="mt-3">
                        <OrgSwitch
                          on={orgDraft.service_mode === "active"}
                          label={`Signals for ${orgName}`}
                          onChange={on =>
                            onOrgDraftChange({
                              ...orgDraft,
                              service_mode: on ? "active" : "inactive",
                            })
                          }
                        />
                      </div>
                      <p className="mt-2 text-[12px] leading-relaxed text-gray-600">
                        {orgDraft.service_mode === "active"
                          ? "Its users see signal cards, counts and history, under the Open Actions setting beside this one."
                          : "Signals keep being evaluated for this organization, but its users see nothing — no cards, counts, history or “turned off” notice."}
                      </p>
                    </article>
                  ) : (
                    <article className="rounded-lg border border-gray-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
                            Organization operating mode
                          </div>
                          <h4 className="mt-1 text-[14px] font-bold text-gray-900">
                            Signal Service
                          </h4>
                        </div>
                        <StatusPill
                          tone={
                            orgPresentation.config.service_mode === "active"
                              ? "good"
                              : "muted"
                          }
                        >
                          {VALUE_LABEL[orgPresentation.config.service_mode]}
                        </StatusPill>
                      </div>
                      <div className="mt-3">
                        <Segmented
                          label="Organization signal service"
                          value={orgDraft.service_mode}
                          options={ORG_MODE_OPTIONS}
                          onChange={value =>
                            onOrgDraftChange({
                              ...orgDraft,
                              service_mode: value,
                            })
                          }
                        />
                      </div>
                      <p className="mt-2 text-[12px] leading-relaxed text-gray-600">
                        {orgModeOption?.description}
                      </p>
                    </article>
                  )}

                  <article className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-dark">
                          Organization presentation
                        </div>
                        <h4 className="mt-1 text-[14px] font-bold text-gray-900">
                          Open Actions
                        </h4>
                      </div>
                      <StatusPill
                        tone={
                          orgPresentation.presentation
                            .effective_actions_surface === "enabled"
                            ? "good"
                            : "muted"
                        }
                      >
                        {
                          VALUE_LABEL[
                            orgPresentation.presentation
                              .effective_actions_surface
                          ]
                        }
                      </StatusPill>
                    </div>
                    <div className="mt-3">
                      <Segmented
                        label="Organization Open Actions"
                        value={orgDraft.actions_surface}
                        options={SURFACE_OPTIONS}
                        onChange={value =>
                          onOrgDraftChange({
                            ...orgDraft,
                            actions_surface: value,
                          })
                        }
                      />
                    </div>
                    <p className="mt-2 text-[12px] leading-relaxed text-gray-600">
                      {orgSurfaceOption?.description}
                    </p>
                    {orgDraft.service_mode !== "active" && (
                      <div className="mt-2 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                        <Info
                          className="mt-0.5 h-3.5 w-3.5 shrink-0"
                          aria-hidden
                        />
                        {optedIn ? (
                          <span>
                            This organization isn&apos;t turned on — its users
                            see nothing, whatever this setting says.
                          </span>
                        ) : (
                          <span>
                            Inactive overrides this setting for this
                            organization — users see Open Actions as{" "}
                            <strong>{VALUE_LABEL[orgDraftEffective]}</strong>.
                          </span>
                        )}
                      </div>
                    )}
                    {current.config.service_mode !== "active" && (
                      <div className="mt-2 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                        <Info
                          className="mt-0.5 h-3.5 w-3.5 shrink-0"
                          aria-hidden
                        />
                        <span>
                          Platform Signal Service is Inactive — users in this
                          organization stay hidden until the platform is Active
                          again, even if this organization is set to Active.
                        </span>
                      </div>
                    )}
                    {platformTightensOrg && (
                      <div className="mt-2 flex gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
                        <Info
                          className="mt-0.5 h-3.5 w-3.5 shrink-0"
                          aria-hidden
                        />
                        <span>
                          Platform Open Actions is{" "}
                          <strong>{VALUE_LABEL[platformEffective]}</strong> —
                          users in this organization see{" "}
                          <strong>{VALUE_LABEL[orgDraftUserSurface]}</strong>.
                          An organization can be stricter than the platform, not
                          more open.
                        </span>
                      </div>
                    )}
                  </article>
                </div>

                <div
                  className={`mb-4 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-[12px] ${
                    orgDraftVisible
                      ? "border-emerald-200 bg-emerald-50/60 text-emerald-900"
                      : "border-gray-200 bg-white text-gray-700"
                  }`}
                >
                  <span className="font-semibold">
                    {orgDraftVisible
                      ? "This organization’s users see signals."
                      : "This organization’s users see no signal output."}
                  </span>
                  {!orgPresentation.has_override && (
                    <span className="text-gray-500">
                      Matches platform defaults until you apply an override.
                    </span>
                  )}
                  {orgDraftVisible !== orgAppliedVisible && (
                    <span className="ml-auto shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-300">
                      After apply
                    </span>
                  )}
                </div>

                {orgPending.count > 0 && (
                  <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                    <span className="text-[12.5px] font-semibold text-amber-900">
                      {orgPending.count} unapplied organization change
                      {orgPending.count === 1 ? "" : "s"}
                    </span>
                    <span className="text-xs text-amber-800">
                      Applies only to {selectedOrgName || "this organization"}.
                    </span>
                    <div className="ml-auto flex gap-2">
                      <Button
                        variant="white"
                        onClick={() =>
                          onOrgDraftChange({
                            service_mode: orgPresentation.config.service_mode,
                            actions_surface:
                              orgPresentation.config.actions_surface,
                          })
                        }
                      >
                        Discard
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => void openOrgReview()}
                      >
                        Review and apply
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : null}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricTile
                label="Open signals"
                value={overview ? overview.open_signals : "—"}
                detail={
                  overview && overview.open_signals > 0 ? (
                    <span className="flex flex-wrap gap-1">
                      {TIER_CHIPS.filter(
                        t => (overview.open_signals_by_tier[t.key] ?? 0) > 0
                      ).map(t => (
                        <span
                          key={t.key}
                          className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${t.className}`}
                        >
                          {overview.open_signals_by_tier[t.key]} {t.label}
                        </span>
                      ))}
                    </span>
                  ) : (
                    "Active and acknowledged in this organization"
                  )
                }
              />
              <MetricTile
                label="Accounts with signals"
                value={
                  overview ? (
                    <>
                      {overview.accounts_with_open_signals}
                      <span className="text-base font-semibold text-gray-400">
                        {" "}
                        / {overview.total_accounts}
                      </span>
                    </>
                  ) : (
                    "—"
                  )
                }
                detail="Accounts with at least one open signal"
              />
              <MetricTile
                label="Users reached"
                value={overview ? overview.users_with_open_signals : "—"}
                detail="Assigned to an account with an open signal"
              />
              <MetricTile
                label="Last evaluation"
                value={
                  <span className="text-[20px]">
                    {overview
                      ? formatRelative(overview.last_evaluated_at)
                      : "—"}
                  </span>
                }
                detail={
                  overview?.last_evaluated_at
                    ? formatDateTime(overview.last_evaluated_at)
                    : "No evaluation recorded yet"
                }
              />
            </div>
          </>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h3 className="text-[13px] font-bold text-gray-900">
            Recent changes
          </h3>
          <button
            type="button"
            onClick={onOpenAudit}
            className="text-xs font-semibold text-brand hover:underline"
          >
            View Change Audit →
          </button>
        </div>
        {recentVersions.length === 0 ? (
          <p className="px-5 py-6 text-[12.5px] text-gray-500">
            No changes yet. The platform is on the default configuration: signal
            service Active, Open Actions Enabled.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {recentVersions.slice(0, 5).map(v => (
              <li key={v.version} className="flex items-start gap-4 px-5 py-3">
                <span className="mt-0.5 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gray-600">
                  v{v.version}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-gray-900">
                    {v.changes.length > 3
                      ? `${v.changes.length} changes: ${v.changes
                          .slice(0, 2)
                          .map(c => c.label)
                          .join(", ")}, …`
                      : v.changes
                          .map(
                            c =>
                              `${c.label}: ${valueLabel(c.from)} → ${valueLabel(c.to)}`
                          )
                          .join(" · ")}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-gray-500">
                    “{v.reason}”
                  </div>
                </div>
                <div className="shrink-0 text-right text-[11.5px] text-gray-500">
                  <div>{formatRelative(v.applied_at)}</div>
                  <div className="text-gray-400">
                    {v.applied_by_email ?? "Izba admin"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <CommonModal
        isOpen={orgReviewOpen}
        toggle={() => !orgApplying && setOrgReviewOpen(false)}
        title={
          orgClearing
            ? "Use platform defaults"
            : orgSwitchTo
              ? `${orgSwitchTo === "active" ? "Turn on" : "Turn off"} signals for ${orgName}`
              : "Apply organization presentation"
        }
        subtitle={
          <span className="text-[13px] font-normal text-gray-500">
            {selectedOrgName || "Selected organization"} only
          </span>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="white"
              disabled={orgApplying}
              onClick={() => setOrgReviewOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!orgReason.trim() || !!orgPreviewError}
              loading={orgApplying}
              onClick={() => void applyOrgPresentation()}
            >
              {orgClearing ? "Use platform defaults" : "Apply to organization"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-[13px] text-gray-600">
            {orgClearing
              ? optedIn
                ? "Removes this organization's override. Under “Only organizations turned on” that leaves it not turned on: its users see no signals until you turn it on again."
                : "Removes this organization's override. It will follow the platform's Signal Service and Open Actions again, including future platform changes."
              : orgSwitchTo
                ? `Sets “Signals for this organization” to ${TURNED_ON_LABEL[orgSwitchTo]} for ${orgName} only. ${
                    orgSwitchTo === "active"
                      ? "Its users start seeing signal cards, counts and history, under its Open Actions setting. Nothing is re-evaluated: signals collected so far appear straight away."
                      : "Its users stop seeing signals, history included. Evaluation carries on in the background."
                  }`
                : "These settings apply only to this organization. Platform layers, rules and CTAs are unchanged."}
          </p>
          {orgPreviewChanges === null && !orgPreviewError && (
            <div className="flex items-center gap-2 text-[12.5px] text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Validating changes…
            </div>
          )}
          {orgPreviewError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {orgPreviewError}
            </div>
          )}
          {orgPreviewChanges && orgPreviewChanges.length > 0 && (
            <ul className="flex max-h-[260px] flex-col gap-1.5 overflow-y-auto">
              {orgPreviewChanges.map(c => {
                const row = changeRow(c);
                return (
                  <li
                    key={c.field}
                    className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 text-[12.5px]"
                  >
                    <span className="min-w-0 font-semibold text-gray-700">
                      {row.label}
                    </span>
                    <span className="shrink-0 whitespace-nowrap">
                      <span className="text-gray-400">{row.from}</span>
                      <span className="mx-2 text-gray-300">→</span>
                      <span className="font-bold text-gray-900">{row.to}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <label className="flex flex-col gap-1.5 text-[12.5px]">
            <span className="font-semibold text-gray-700">Reason</span>
            <textarea
              value={orgReason}
              onChange={e => setOrgReason(e.target.value)}
              rows={3}
              className="rounded-lg border border-gray-200 px-3 py-2 text-[13px] outline-none ring-brand/30 focus:ring-2"
              placeholder={
                orgClearing
                  ? "Why should this organization follow the platform again?"
                  : "Why is this organization changing?"
              }
            />
          </label>
          {orgApplyError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {orgApplyError}
            </div>
          )}
        </div>
      </CommonModal>
    </div>
  );
};

export default OverviewPanel;
